import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { blogsData } from './BlogPageSection'
import CTA from './CTA'

export default function BlogDetail () {
  const { id } = useParams()
  const blog = blogsData.find(item => item.id === id)
  const [openFAQ, setOpenFAQ] = useState(null)

  if (!blog) {
    return <div className='py-32 text-center text-xl'>Blog not found</div>
  }

  return (
    <>
      {/* ================= SEO ================= */}
      <Helmet>
        <title>{blog.metaTitle || blog.title}</title>
        <meta name='description' content={blog.metaDescription} />
        <meta name='keywords' content={blog.keywords?.join(', ')} />

        {/* Open Graph */}
        <meta property='og:title' content={blog.metaTitle || blog.title} />
        <meta property='og:description' content={blog.metaDescription} />
        <meta property='og:image' content={blog.image} />
        <meta property='og:type' content='article' />

        {/* Twitter */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' content={blog.metaTitle || blog.title} />
        <meta name='twitter:description' content={blog.metaDescription} />
        <meta name='twitter:image' content={blog.image} />

        {/* Article Schema */}
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: blog.title,
            description: blog.metaDescription,
            image: blog.image,
            author: {
              '@type': 'Organization',
              name: blog.author || 'S Infinite'
            },
            publisher: {
              '@type': 'Organization',
              name: 'S Infinite',
              logo: {
                '@type': 'ImageObject',
                url: 'https://yourdomain.com/logo.png'
              }
            },
            datePublished: blog.date
          })}
        </script>

        {/* FAQ Schema */}
        {blog.faqs && (
          <script type='application/ld+json'>
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: blog.faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      {/* ================= MAIN BLOG ================= */}
      <section className='bg-white py-24'>
        <div className='max-w-3xl mx-auto px-6'>
          {/* Back */}
          <Link
            to='/blogs'
            className='text-sm font-medium text-gray-500 hover:text-black transition mb-5 inline-block'
          >
            ← Back to all articles
          </Link>

          {/* Title */}
          <h1 className='text-4xl md:text-3xl font-extrabold text-[#1B1A4B] leading-tight'>
            {blog.title}
          </h1>

          {/* Meta */}
          {/* <div className="mt-2 flex items-center gap-4 text-sm text-blue-600">
            {blog.author && <span>{blog.tag} - {blog.date}</span>}
          </div> */}

          {/* Image */}
          <div
            className='mt-3 rounded-xl overflow-hidden shadow-lg'
            onContextMenu={e => e.preventDefault()}
          >
            <img
              src={blog.image}
              alt={blog.title}
              draggable='false'
              className='w-full h-full object-cover select-none pointer-events-none'
            />
          </div>

          {/* Introduction */}
          {blog.introduction && (
            <p className='mt-2 text-lg text-black font-bold leading-relaxed'>
              {blog.introduction}
            </p>
          )}
          <div className='mt-2 flex items-center  text-sm text-blue-600'>
            {blog.author && (
              <span>
                {blog.tag} - {blog.date}
              </span>
            )}
          </div>
          <div className='mt-2 border-t border-gray-400' />

          {/* Sections Grid Layout */}
          <div className='mt-4 relative'>
            {/* Grid */}
            <div className='grid md:grid-cols-2 gap-10 relative'>
              {blog.sections?.slice(0, 4).map((section, index) => (
                <div key={index} className='relative pb-5'>
                  {/* Heading */}
                  <h2 className='text-xl md:text-2xl font-bold text-red-600 mb-4'>
                    {section.heading}
                  </h2>

                  {/* Paragraphs */}
                  {section.paragraphs?.map((para, i) => (
                    <p
                      key={i}
                      className='text-sm text-gray-700 leading-relaxed mb-2'
                    >
                      {para}
                    </p>
                  ))}

                  {/* Bullets */}
                  {section.bullets?.length > 0 && (
                    <ul className='space-y-2 mt-1'>
                      {section.bullets.map((point, i) => (
                        <li
                          key={i}
                          className='flex items-start gap-3 text-sm font-semibold text-gray-900'
                        >
                          <span className='mt-2 w-2 h-2 bg-[#1B1A4B] rounded-full' />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Vertical Divider */}
            {/* <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-[2px] bg-red-600"></div> */}

            {/* Horizontal Divider */}
            {/* <div className="hidden md:block absolute left-0 right-0 top-1/2 h-[2px] bg-red-600"></div> */}
          </div>

          {/* Conclusion */}
          {blog.conclusion && (
            <div className='mt-6 bg-gray-50 border border-gray-200 p-8 rounded-2xl'>
              <h3 className='text-xl font-bold text-[#1B1A4B] mb-4'>
                Conclusion
              </h3>
              <p className='text-lg text-gray-800 leading-relaxed'>
                {blog.conclusion}
              </p>
            </div>
          )}

          {/* FAQs */}
          {blog.faqs && (
            <div className='mt-6'>
              <h3 className='text-2xl font-bold text-[#1B1A4B] mb-4'>
                Frequently Asked Questions
              </h3>

              <div className='space-y-4'>
                {blog.faqs.map((faq, index) => (
                  <div
                    key={index}
                    className='border border-gray-200 rounded-xl overflow-hidden'
                  >
                    <button
                      onClick={() =>
                        setOpenFAQ(openFAQ === index ? null : index)
                      }
                      className='w-full text-left p-5 flex justify-between items-center font-semibold text-gray-800'
                    >
                      {faq.question}
                      <span>{openFAQ === index ? '−' : '+'}</span>
                    </button>

                    {openFAQ === index && (
                      <div className='px-5 pb-5 text-gray-600 text-base leading-relaxed'>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ================= EXPLORE MORE ================= */}
      <section className='bg-gradient-to-r from-[#fffcb0] via-[#fff86f] to-[#fffcb0] py-6'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='flex items-center justify-between mb-8'>
            <h2 className='text-2xl font-bold text-[#1B1A4B]'>
              Explore More Blogs
            </h2>

            <Link
              to='/blogs'
              className='text-sm font-semibold text-blue-600 hover:underline'
            >
              View All →
            </Link>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
            {blogsData
              .filter(item => item.id !== id) // skip current blog
              .slice(0, 4)
              .map(item => (
                <Link
                  to={`/blogs/${item.id}`}
                  key={item.id}
                  className='group bg-white rounded-2xl shadow-md hover:shadow-xl transition overflow-hidden'
                >
                  <div
                    className='h-40 bg-gray-100 overflow-hidden'
                    onContextMenu={e => e.preventDefault()}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className='w-full h-auto object-cover group-hover:scale-105 transition duration-300 select-none pointer-events-none'
                    />
                  </div>

                  <div className='p-4'>
                    <span className='text-xs font-semibold text-blue-600'>
                      Publish by S INFINITE
                    </span>

                    <h3 className='mt-2 text-sm font-bold text-gray-900 line-clamp-2'>
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <CTA />
    </>
  )
}
